import {
  formatNumber as ng_formatNumber,
  formatCurrency as ng_formatCurrency,
  getCurrencySymbol,
} from '@angular/common';

import { DecimalConfig } from 'v2/modules/shared/configs/decimal.config';

//

export const toFiniteOrNull = (
  value :unknown,
  coerce :(string :string) => number = parseFloat,
) :number | null => {
  // coerce string to number
  if (typeof value === 'string') {
    value = coerce(value as string);
  }

  return typeof value === 'number' && Number.isFinite(value)
    ? (value as number)
    : null;
};

//
// SORT COMPARATORS
// =====

/**
 * @param radix - Passed as the radix to `parseInt()`.
 */
export const compareAsIntegersFactory = (radix :number): ((a, b) => number) => {
  return (a, b) => parseInt(a, radix) - parseInt(b, radix);
};

export const compareAsFloats = (a, b) => parseFloat(a) - parseFloat(b);
export const compareAsIntegers = compareAsIntegersFactory(10);
export const compareAsCoercedNumbers = (a, b) => Number(a) - Number(b);

//
// FORMATTERS
// =====

const _digitsInfoFromDigits = (
  digits :string | number,
  digitsInfo = '1.0-6',
  exact = false,
) :string => {
  if (typeof digits === 'string') {
    digitsInfo = digits;
  } else if (typeof digits === 'number' && Number.isFinite(digits)) {
    digitsInfo = exact ? `1.${digits}-${digits}` : `1.0-${digits}`;
  }

  return digitsInfo;
};

export const formatCount = (value :any, options ?:{ locale ?:string }) :string => {
  value = toFiniteOrNull(value);

  return value === null ? '' : formatCountStrict(value, options);
};
export const formatCountStrict = (
  value :string | number,
  options ?:{ locale ?:string },
) :string => {
  options = {
    locale: 'en-US',
    ...options,
  };

  return ng_formatNumber(value as any, options.locale, '1.0-0');
};

export const formatCurrency = (
  value :any,
  options ?:{
    currencyCode ?:string;
    /** Worked out based on the locale if omitted (recommended). */
    digits ?:string | number;
    /** Ignored if `symbol` is provided. */
    display ?:'code' | 'symbol' | 'symbol-narrow' | null;
    locale ?:string;
    symbol ?:string;
  },
) :string => {
  value = toFiniteOrNull(value);

  return value === null ? '' : formatCurrencyStrict(value, options);
};
export const formatCurrencyStrict = (
  value :string | number,
  options ?:{
    currencyCode ?:string;
    /** If omitted (recommended), this is worked out based on the locale. */
    digits ?:string | number;
    /**
     * If set, `options.symbol` is ignored and the symbol is worked out similar
     * to how Angular's `CurrencyPipe` does.
     */
    display ?:'code' | 'symbol' | 'symbol-narrow' | null;
    locale ?:string;
    /** Ignored if `options.display` is set. */
    symbol ?:string;
  },
) :string => {
  options = {
    currencyCode: 'USD',
    locale: 'en-US',
    symbol: '$',
    ...options,
  };

  options.currencyCode ??= 'USD';
  options.locale ??= 'en-US';

  // see https://github.com/angular/angular/blob/15.2.10/packages/common/src/pipes/number_pipe.ts#L170-L278
  const currency = options.display
    ? options.display === 'code'
      ? options.currencyCode
      : getCurrencySymbol(
          options.currencyCode,
          options.display === 'symbol-narrow' ? 'narrow' : 'wide',
          options.locale,
        )
    : (options.symbol ?? '$');

  return ng_formatCurrency(
    value as any,
    options.locale,
    currency,
    options.currencyCode,
    options.digits ? _digitsInfoFromDigits(options.digits, '1.2-2', true) : null,
  );
};

export const formatQuantity = (
  value :any,
  options ?:{
    digits ?:string | number;
    locale ?:string;
  },
) :string => {
  value = toFiniteOrNull(value);

  return value === null ? '' : formatQuantityStrict(value, options);
};
export const formatQuantityStrict = (
  value :string | number,
  options ?:{
    digits ?:string | number;
    locale ?:string;
  },
) :string => {
  options = {
    digits: DecimalConfig.defaultPlaces,
    locale: 'en-US',
    ...options,
  };

  return ng_formatNumber(
    value as any,
    options.locale,
    _digitsInfoFromDigits(options.digits),
  );
};
